import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ExtraTool {
  id: string;
  icon: string;
  titleKey: string;
  descKey: string;
  iconBg: string;
  iconColor: string;
  route?: string;
  comingSoon?: boolean;
}

const extraTools: ExtraTool[] = [
  {
    id: 'bg_clean',
    icon: 'ri-eraser-fill',
    titleKey: 'extool_bgClean',
    descKey: 'extool_bgClean_desc',
    iconBg: 'linear-gradient(135deg, #14B8A6, #2DD4BF)',
    iconColor: '#14B8A6',
    route: '/bg-clean',
  },
  {
    id: 'cloth_extract',
    icon: 'ri-t-shirt-air-fill',
    titleKey: 'extool_clothExtract',
    descKey: 'extool_clothExtract_desc',
    iconBg: 'linear-gradient(135deg, #F43F5E, #FB7185)',
    iconColor: '#F43F5E',
    route: '/cloth-extract',
  },
  {
    id: 'watermark_remove',
    icon: 'ri-contrast-drop-fill',
    titleKey: 'extool_watermarkRemove',
    descKey: 'extool_watermarkRemove_desc',
    iconBg: 'linear-gradient(135deg, #6366F1, #818CF8)',
    iconColor: '#6366F1',
    route: '/watermark-remove',
  },
  {
    id: 'colorize',
    icon: 'ri-palette-fill',
    titleKey: 'extool_colorize',
    descKey: 'extool_colorize_desc',
    iconBg: 'linear-gradient(135deg, #EC4899, #F472B6)',
    iconColor: '#EC4899',
    route: '/colorize',
  },
  {
    id: 'object_remove',
    icon: 'ri-delete-back-fill',
    titleKey: 'extool_objectRemove',
    descKey: 'extool_objectRemove_desc',
    iconBg: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
    iconColor: '#F59E0B',
    route: '/object-remove',
  },
  {
    id: 'style_transfer',
    icon: 'ri-brush-3-fill',
    titleKey: 'extool_styleTransfer',
    descKey: 'extool_styleTransfer_desc',
    iconBg: 'linear-gradient(135deg, #A855F7, #C084FC)',
    iconColor: '#A855F7',
    route: '/style-transfer',
  },
];

export default function MoreTools() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-dvh font-nunito" style={{ background: 'linear-gradient(160deg, #FFF5F9 0%, #F8F3FF 50%, #F3F6FF 100%)' }}>
      <div className="relative max-w-md mx-auto min-h-dvh flex flex-col">
        {/* Header */}
        <div
          className="relative z-10 flex items-center gap-3 px-4 pt-safe pb-3"
        >
          <button
            onClick={() => navigate('/')}
            className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-95 transition-transform"
            style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)' }}
          >
            <i className="ri-arrow-left-line text-gray-600 text-base" />
          </button>
          <h1 className="text-base font-bold text-gray-800">{t('extool_title')}</h1>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto ios-scroll px-5 pb-36">
          {/* Subtitle */}
          <p className="text-xs text-gray-400 mb-4 leading-relaxed">
            {t('extool_subtitle')}
          </p>

          {/* Tools grid */}
          <div className="grid grid-cols-2 gap-3">
            {extraTools.map((tool, idx) => (
              <button
                key={tool.id}
                onClick={() => {
                  if (tool.route && !tool.comingSoon) {
                    navigate(`${tool.route}?from=tools`, { state: { returnTo: '/tools' } });
                  }
                }}
                disabled={tool.comingSoon}
                className="relative flex flex-col gap-3 p-4 rounded-2xl text-left cursor-pointer active:scale-[0.97] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: '#ffffff',
                  border: '1px solid rgba(0,0,0,0.04)',
                  animation: tool.comingSoon ? undefined : `fadeInUp 0.5s ease both ${idx * 0.05}s`,
                }}
              >
                {/* Icon */}
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: tool.iconBg,
                    boxShadow: `0 3px 10px ${tool.iconColor}33`,
                  }}
                >
                  <i className={`${tool.icon} text-white text-lg`} />
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-sm font-bold text-gray-800 whitespace-nowrap">
                      {t(tool.titleKey)}
                    </h3>
                    {tool.comingSoon && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold text-amber-600 bg-amber-50 whitespace-nowrap">
                        {t('extool_comingSoon')}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1 leading-snug break-words">
                    {t(tool.descKey)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
